import {
  DollarSign,
  HelpCircle,
  Mail,
  MessageSquare,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import { Modal } from "../ui/Modal";
import {
  HEALTHMATCH_PRIVACY_URL,
  HEALTHMATCH_TERMS_URL,
} from "../../utils/urlConfig";

interface DataUsageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function ModalFooter({ onClose }: { onClose: () => void }) {
  return (
    <div className="mt-8">
      <div className="flex justify-between mb-4">
        <a
          href={HEALTHMATCH_TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary-600 hover:text-primary-500"
        >
          Terms and Conditions
        </a>
        <a
          href={HEALTHMATCH_PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary-600 hover:text-primary-500"
        >
          Privacy policy
        </a>
      </div>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function UsageListItem({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-3 py-2">
      <span className="text-gray-400 mt-0.5 flex-shrink-0">{icon}</span>
      <span className="text-sm text-gray-600">{children}</span>
    </li>
  );
}

export function EmailUsageModal({ isOpen, onClose }: DataUsageModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="How we use your email">
      <p className="text-sm text-gray-700 mb-4">
        Your email is needed to log in and receive updates on your applications.
        You'll also get alerts about new trial matches.
      </p>
      <ul className="space-y-1 mb-4">
        <UsageListItem icon={<X className="w-5 h-5" />}>
          Unsubscribe anytime, click <strong>Unsubscribe</strong> in any email
        </UsageListItem>
        <UsageListItem icon={<HelpCircle className="w-5 h-5" />}>
          Email{" "}
          <a
            href="mailto:hello@healthmatch.io"
            className="text-primary-600 hover:text-primary-500"
          >
            hello@healthmatch.io
          </a>{" "}
          for support
        </UsageListItem>
      </ul>
      <ModalFooter onClose={onClose} />
    </Modal>
  );
}

export function PhoneUsageModal({ isOpen, onClose }: DataUsageModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="How we use your phone number"
    >
      <p className="text-sm text-gray-700 mb-4">
        HealthMatch may contact you about new clinical trials that you match or
        call you regarding a specific application you have made. These messages
        may include automated texts or calls.
      </p>
      <ul className="space-y-1 mb-4">
        <UsageListItem icon={<MessageSquare className="w-5 h-5" />}>
          Message frequency varies
        </UsageListItem>
        <UsageListItem icon={<Settings className="w-5 h-5" />}>
          These messages may include automated texts or calls
        </UsageListItem>
        <UsageListItem icon={<DollarSign className="w-5 h-5" />}>
          Standard message and data rates may apply
        </UsageListItem>
        <UsageListItem icon={<X className="w-5 h-5" />}>
          Unsubscribe anytime, text <strong>'STOP'</strong> to cancel
        </UsageListItem>
        <UsageListItem icon={<Trash2 className="w-5 h-5" />}>
          You can delete your profile at any time
        </UsageListItem>
        <UsageListItem icon={<HelpCircle className="w-5 h-5" />}>
          Email{" "}
          <a
            href="mailto:hello@healthmatch.io"
            className="text-primary-600 hover:text-primary-500"
          >
            hello@healthmatch.io
          </a>{" "}
          for support
        </UsageListItem>
      </ul>
      <ModalFooter onClose={onClose} />
    </Modal>
  );
}

export function AddressUsageModal({ isOpen, onClose }: DataUsageModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="How we use your address">
      <p className="text-sm text-gray-700 mb-4">
        We need your address to help determine your eligibility for this trial
        and to facilitate the collection of your medical records.
      </p>
      <ul className="space-y-1 mb-4">
        <UsageListItem icon={<Mail className="w-5 h-5" />}>
          We will never send you anything in the mail or use your address for
          marketing purposes.
        </UsageListItem>
        <UsageListItem icon={<HelpCircle className="w-5 h-5" />}>
          Email{" "}
          <a
            href="mailto:hello@healthmatch.io"
            className="text-primary-600 hover:text-primary-500"
          >
            hello@healthmatch.io
          </a>{" "}
          for support
        </UsageListItem>
      </ul>
      <ModalFooter onClose={onClose} />
    </Modal>
  );
}
